import React, { useState, useEffect, useRef } from "react";
import { Option } from "Models/checklist";
import useStyles, {
  EditOptionButton,
  OptionTag,
  OptionName,
  QuestionOptionsContainer,
} from "./styles";
import { Edit } from "iconsax-react";

interface OptionsNamesTagsProps {
  tags: Option[];
  handleOpenDrawer: () => void;
  isOnViewMode: boolean;
}

export const OptionsTags = ({ tags, handleOpenDrawer, isOnViewMode }: OptionsNamesTagsProps) => {
  const tagsContainerRef = useRef<HTMLDivElement>(null);

  const [visibleTags, setVisibleTags] = useState<Option[]>([]);
  const [overflowTagsCount, setOverflowTagsCount] = useState(0);

  const classes = useStyles();

  useEffect(() => {
    const calculateVisibleTags = () => {
      if (!tagsContainerRef.current) return;

      const containerWidth = tagsContainerRef.current.offsetWidth;

      let currentWidth = 0;
      let visibleCount = 0;

      // Create a temporary element to measure the width of each tag
      const measureTagWidth = (name: string) => {
        const tempTag = document.createElement("div");
        tempTag.className = classes.optionTag;
        tempTag.style.position = "absolute";
        tempTag.style.visibility = "hidden";
        tempTag.textContent = name;
        document.body.appendChild(tempTag);
        const width = tempTag.offsetWidth;
        document.body.removeChild(tempTag);
        return width;
      };

      const plusAndEditButtonWidth = 150; // Approximate width for "+" counter and edit button

      for (let tagIndex = 0; tagIndex < tags.length; tagIndex++) {
        const tagWidth = measureTagWidth(tags[tagIndex].name);

        const isLast = tagIndex === tags.length - 1;
        const totalWidthWithNextTag = currentWidth + tagWidth;
        const totalWidthWithPlusButton = totalWidthWithNextTag + plusAndEditButtonWidth;

        if (totalWidthWithPlusButton > containerWidth && !isLast) {
          // If adding the next tag, the "+" counter and the edit button exceeds the container width
          setOverflowTagsCount(tags.length - tagIndex);
          setVisibleTags(tags.slice(0, tagIndex));
          return;
        } else if (totalWidthWithNextTag > containerWidth) {
          // If adding the next tag exceeds the container width
          setOverflowTagsCount(tags.length - tagIndex);
          setVisibleTags(tags.slice(0, tagIndex));
          return;
        }

        currentWidth += tagWidth;
        visibleCount++;
      }

      // If all tags fit
      setOverflowTagsCount(0);
      setVisibleTags(tags);
    };

    calculateVisibleTags();

    window.addEventListener("resize", calculateVisibleTags);

    return () => window.removeEventListener("resize", calculateVisibleTags);
  }, [tags]);

  return (
    <QuestionOptionsContainer ref={tagsContainerRef}>
      {visibleTags.map((option, index) => {
        return (
          <OptionTag key={index}>
            <OptionName>{option.name}</OptionName>
          </OptionTag>
        );
      })}

      {overflowTagsCount > 0 && (
        <OptionTag>
          <OptionName>+{overflowTagsCount}</OptionName>
        </OptionTag>
      )}

      <EditOptionButton disabled={isOnViewMode} onClick={() => handleOpenDrawer()}>
        <Edit size={16} />
      </EditOptionButton>
    </QuestionOptionsContainer>
  );
};
